import { useState, useRef, useEffect, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AuthContext } from "components/Context/AuthContext";
import { useAppContext } from "components/Context/AppContext";
import ImpersonateModal from "components/ImpersonateModal";

/** Derive a display name from an email address.
 *  "john.doe@company.com" → "John Doe"
 *  "jsmith@company.com"   → "Jsmith"
 */
function displayNameFromEmail(email) {
  if (!email) return "—";
  const prefix = email.split("@")[0];
  return prefix
    .split(/[._-]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export default function DashboardTopbar() {
  const { logout } = useContext(AuthContext);
  const { impersonating, startImpersonation, endImpersonation } = useAppContext();
  const navigate = useNavigate();
  const [userOpen,        setUserOpen]        = useState(false);
  const [impersonateOpen, setImpersonateOpen] = useState(false);
  const userRef = useRef(null);

  const email       = sessionStorage.getItem("username") || "";
  const displayName = displayNameFromEmail(email);
  const isSuperadmin = sessionStorage.getItem("userRole") === "superadmin";

  const today = new Date().toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });

  useEffect(() => {
    const handler = (e) => {
      if (userRef.current && !userRef.current.contains(e.target)) {
        setUserOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleChangePassword = () => {
    setUserOpen(false);
    navigate("/resetPasswordafterlogin");
  };

  const handleLogout = () => {
    setUserOpen(false);
    logout();
    navigate("/home");
  };

  const handleImpersonateOpen = () => {
    setUserOpen(false);
    setImpersonateOpen(true);
  };

  return (
    <>
    <header className="trg-topbar">
      <Link to="/home" className="trg-logo">
        <div className="trg-logo-mark">
          <svg width="16" height="12" viewBox="0 0 32 23" fill="none">
            <path
              d="M19.45 3.78L24.71 9.04L26.6 7.15L19.45 0L14.81 4.64L28.04 17.87L27.16 18.75L13.96 5.54L0 19.5L1.89 21.39L13.96 9.33L27.16 22.53L31.82 17.87L18.6 4.64L19.45 3.78Z"
              fill="#C9A84C"
            />
          </svg>
        </div>
        Tail Risk Global
      </Link>

      <div className="trg-topbar-right">
        {impersonating ? (
          <span style={{
            display: "flex", alignItems: "center", gap: 8,
            background: "#7a4f00", color: "#ffd580",
            padding: "3px 10px", borderRadius: 4, fontSize: 12, fontWeight: 600,
          }}>
            Acting as: {impersonating}
            <button
              onClick={endImpersonation}
              style={{
                background: "none", border: "1px solid #ffd580", color: "#ffd580",
                borderRadius: 3, padding: "1px 7px", cursor: "pointer", fontSize: 11,
              }}
            >
              End Session
            </button>
          </span>
        ) : null}
        <span className="trg-topbar-date">{today}</span>

        {/* User icon + dropdown */}
        <div
          className="trg-topbar-icon"
          ref={userRef}
          onClick={() => setUserOpen((o) => !o)}
          title="Account"
        >
          <svg
            width="13" height="13" viewBox="0 0 24 24"
            fill="none" stroke="#6b80a0" strokeWidth="2" strokeLinecap="round"
          >
            <circle cx="12" cy="8" r="4" />
            <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
          </svg>

          {userOpen && (
            <div className="trg-user-dropdown" onClick={(e) => e.stopPropagation()}>

              {/* Header: person icon + name + email */}
              <div className="trg-user-header">
                <div className="trg-user-avatar">
                  <svg
                    width="18" height="18" viewBox="0 0 24 24"
                    fill="none" stroke="var(--dash-muted)" strokeWidth="1.5" strokeLinecap="round"
                  >
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                  </svg>
                </div>
                <div className="trg-user-info">
                  <div className="trg-user-name" title={displayName}>{displayName}</div>
                  <div className="trg-user-email" title={email}>{email}</div>
                </div>
              </div>

              <div className="trg-user-divider" />

              {/* Actions */}
              <div className="trg-user-item" onClick={handleChangePassword}>
                <svg
                  width="11" height="11" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                  style={{ flexShrink: 0 }}
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                Change Password
              </div>

              {isSuperadmin && !impersonating && (
                <div className="trg-user-item" onClick={handleImpersonateOpen}>
                  <svg
                    width="11" height="11" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                    style={{ flexShrink: 0 }}
                  >
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                  Admin View
                </div>
              )}

              <div className="trg-user-item trg-user-item--danger" onClick={handleLogout}>
                <svg
                  width="11" height="11" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                  style={{ flexShrink: 0 }}
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                Logout
              </div>

            </div>
          )}
        </div>
      </div>
    </header>

    <ImpersonateModal
      isOpen={impersonateOpen}
      onClose={() => setImpersonateOpen(false)}
      onSuccess={(targetUsername, token) => {
        setImpersonateOpen(false);
        startImpersonation(targetUsername, token);
      }}
    />
    </>
  );
}
